import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router'

import { t } from '@ctcells/i18n'

import { useModalBusy, useModalFrame } from '../atoms/modalFrame.ts'
import { PageHeader } from '../layout/PageHeader.tsx'

import './screens.css'
import './resource.css'

/**
 * 화면이 공유하는 뼈대 조각 — 리소스·시스템 화면이 주 사용자이고
 * 「연결 끊김」 표시(`OfflineNotice`)는 운영 화면(알람)도 같이 쓴다.
 *
 * 목록 화면은 `ListPage`(§14-1)를 그대로 쓰고, **상세 화면만** 여기 `DetailPage` 를 쓴다.
 * `ListPage` 는 주 버튼 자리를 스스로 쥐는데(탭 줄 또는 검색 툴바), 상세 화면의 버튼 줄은
 * 와이어프레임에서 제목 오른쪽(`.ahead` 우측)이라 자리가 다르다.
 */

export interface CardProps {
  /** 카드 제목. 없으면 제목 줄을 두지 않는다 */
  title?: string | undefined
  /** 제목 오른쪽 슬롯(카운트 칩 등) */
  titleEnd?: ReactNode
  children: ReactNode
}

/** `.acard` 493행 — 화면 안 묶음 하나. 설명 문장 슬롯을 두지 않았다(§1-1) */
export function Card({ title, titleEnd, children }: CardProps) {
  return (
    <section className="card">
      {title === undefined ? null : (
        <div className="card__title">
          {title}
          {titleEnd}
        </div>
      )}
      {children}
    </section>
  )
}

export interface DetailPageProps {
  title: ReactNode
  /** 제목 옆 배지 */
  badge?: ReactNode
  /** 제목 «밖» · 제목 줄 오른쪽 끝(`PageHeader.titleAside`) — 제목의 접근성 이름에 섞이지 않는다 */
  titleAside?: ReactNode
  /** 제목 오른쪽 버튼 줄 */
  actions?: ReactNode
  footer?: ReactNode
  /** 돌아갈 곳. 문구는 출처를 기억한다(§14-1) */
  backTo?: string | undefined
  backLabel?: string | undefined
  /** 제목 아래 한 줄 메타. 설명이 아니라 값이다 */
  submeta?: ReactNode
  /**
   * 서랍(목록 오른쪽 판)으로 열렸을 때의 닫기. 넘기면 「← 목록」 줄 대신 **오른쪽 위 ×** 가 선다
   * (eric 2026-09-22 「닫기는 오른쪽 위에 X 로 있어야지」).
   */
  onClose?: (() => void) | undefined
  /**
   * 저장 중. 창(`Modal`) 안에서는 창이 닫히지 않는다(× · Esc · 바깥 누름). 페이지에서는 쓰지 않는다
   */
  busy?: boolean | undefined
  children: ReactNode
}

/**
 * 상세 화면 뼈대 — `.back` + `.ahead` + 스크롤 본문.
 * 스크롤이 생기는 곳은 `.apage` 하나다(§7).
 *
 * 🔴 **창(`Modal`) 안에 들면 창 모양으로 선다**(eric 2026-09-30 「추가 창을 팝업으로」). 제목 · 「← 목록」 ·
 *    × 는 창의 머리줄이 맡으므로 그리지 않고, 버튼 줄(`actions`)은 **창 바닥 줄**로 옮긴다 — 본문이
 *    스크롤돼도 「만들기」가 늘 보인다. 화면 컴포넌트는 창인지 몰라도 된다.
 */
export function DetailPage({
  title,
  badge,
  titleAside,
  actions,
  footer,
  backTo,
  backLabel,
  submeta,
  onClose,
  busy = false,
  children,
}: DetailPageProps) {
  const modal = useModalFrame()
  useModalBusy(busy)

  if (modal !== null) {
    return (
      <>
        {submeta === undefined ? null : <div className="submeta">{submeta}</div>}
        <div className="apage apage--modal">{children}</div>
        {(actions === undefined && footer === undefined) || modal.footer === null
          ? null
          : createPortal(
              <>
                {actions}
                {footer}
              </>,
              modal.footer,
            )}
      </>
    )
  }

  return (
    <div className="detailframe">
      <PageHeader
        title={title as string}
        onClose={onClose}
        {...(badge === undefined ? {} : { badge })}
        {...(titleAside === undefined ? {} : { titleAside })}
        {...(actions === undefined ? {} : { actions })}
        {...(backTo === undefined || backLabel === undefined
          ? {}
          : { back: <BackLink label={backLabel} to={backTo} /> })}
      />
      {submeta === undefined ? null : <div className="submeta">{submeta}</div>}
      <div className="apage">{children}</div>
      {footer}
    </div>
  )
}

/** 「← 목록명」. 출처를 기억한 문구를 그대로 받는다 */
export function BackLink({ label, to }: { label: string; to: string }) {
  const navigate = useNavigate()
  return (
    <button className="back lnk" onClick={() => void navigate(to)} type="button">
      {`← ${label}`}
    </button>
  )
}

/**
 * 표 첫 칸에서 상세로 가는 링크.
 *
 * 🔴 와이어프레임은 행 전체를 눌러 상세를 여는데(`tr.rowlink`) 그건 키보드로 갈 수 없다(§11-4-7).
 *    목록↔상세를 라우트로 올린 우리 구조에서는 **첫 칸을 진짜 버튼으로** 만들어 링크한다.
 *    표 자체는 읽기 전용으로 남는다(§14-4 "목록은 정보를 바꾸는 화면이 아니다").
 */
export function RowLink({
  to,
  children,
  mono,
}: {
  to: string
  children: ReactNode
  mono?: boolean
}) {
  const navigate = useNavigate()
  return (
    <button
      className={`lnk${mono === true ? ' mono' : ''}`}
      onClick={() => void navigate(to)}
      type="button"
    >
      {children}
    </button>
  )
}

/**
 * 🔴 연결 끊김 표시. 툴팁만으로 끝내지 않는다 — CLAUDE.md 규약이 「표시」를 요구한다.
 * 화면마다 같은 자리에 같은 문구로 낸다.
 */
export function OfflineNotice({ shown }: { shown: boolean }) {
  if (!shown) return null
  return (
    <p className="offline" role="status">
      {t('safety.controlDisabled')}
    </p>
  )
}
